import { makeAutoObservable } from 'mobx'
import { MAX_COMPANY_NAME_LENGTH, MAX_ROLE_LENGTH } from 'shared/constants/auth'
import { type IParamsRegisterAnswersStep1 } from 'entities/Auth'
import { type IResponseUsersProfilesUiSettings } from 'entities/Users/api/types'
import {
  CompanySizeEnum,
  FieldsEnum,
  type ICompanySize,
  type IParamsRegisterGtmAnswersStep1,
  type IRole,
  RolesEnum,
} from './type'

export class Step1Store {
  private _role: IRole | null = null
  private _companyName = ''
  private _companySize = ''
  private _activeField: FieldsEnum | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get payload() {
    return {
      organization_name: this.companyName,
      answer_team_size: this.companySize,
      answer_role: this._role?.name || '',
    }
  }

  get answers(): IParamsRegisterAnswersStep1 {
    return {
      org_name: this.companyName,
      company_size: this.companySize,
      role: this._role?.name || '',
    }
  }

  get gtmAnswers(): IParamsRegisterGtmAnswersStep1 {
    return {
      company_size: this.companySize,
      role: this._role?.name || '',
    }
  }

  get isCompletedStep() {
    return !!this.companyName && !!this.companySize && !!this.role
  }

  reset = () => {
    this._role = null
    this._companyName = ''
    this._companySize = ''
    this._activeField = null
  }

  private migrateCompanySize = (value: string): string => {
    if (value === '100+') {
      return CompanySizeEnum.size_100_249
    }

    return value
  }

  initAnswers = ({ data }: IResponseUsersProfilesUiSettings) => {
    if (!data) return

    const { organization_name, answer_team_size, answer_role } = data.items.answers

    if (organization_name) this._companyName = organization_name
    if (answer_team_size) {
      const migratedCompanySize = this.migrateCompanySize(answer_team_size)

      const isValidSize = this.companySizes.some((size) => size.id === migratedCompanySize)
      if (isValidSize) {
        this._companySize = migratedCompanySize
      }
    }
    if (answer_role) {
      const role = this.roles.find((item) => item.id === answer_role)
      if (role) {
        this._role = role
      } else {
        this._role = {
          id: RolesEnum.other,
          name: answer_role,
        }
      }
    }
  }

  get isActiveCompanyNameField() {
    return this._activeField === FieldsEnum.companyName
  }

  get isActiveRoleField() {
    return this._activeField === FieldsEnum.role
  }

  get companyName() {
    return this._companyName
  }

  get companyNameLength() {
    return this._companyName.length
  }

  get isMaxCompanyLengthName() {
    return this.companyNameLength === MAX_COMPANY_NAME_LENGTH
  }

  get companySize() {
    return this._companySize
  }

  get role() {
    return this._role
  }

  get isValidRole() {
    return Boolean(
      this._role && !!this._role.name.length && this._role.name.length <= MAX_COMPANY_NAME_LENGTH
    )
  }

  get roles(): IRole[] {
    return [
      {
        id: RolesEnum.entrepreneur,
        name: 'Entrepreneur',
      },
      {
        id: RolesEnum.salesManager,
        name: 'Sales Manager',
      },
      {
        id: RolesEnum.marketingManager,
        name: 'Marketing Manager',
      },
      {
        id: RolesEnum.customerSuccessManager,
        name: 'Customer Success Manager',
      },
      {
        id: RolesEnum.operationsManager,
        name: 'Operations Manager',
      },
      {
        id: RolesEnum.ITAdministrator,
        name: 'IT Administrator',
      },
      {
        id: RolesEnum.cLevel,
        name: 'C-level',
      },
      {
        id: RolesEnum.vp,
        name: 'VP',
      },
      {
        id: RolesEnum.director,
        name: 'Director',
      },
      {
        id: RolesEnum.manager,
        name: 'Manager',
      },
      {
        id: RolesEnum.individualContributor,
        name: 'Individual contributor',
      },
      {
        id: RolesEnum.other,
        name: 'Other',
      },
    ]
  }

  get companySizes(): ICompanySize[] {
    return [
      {
        id: CompanySizeEnum.justMe,
        name: 'Just me',
      },
      {
        id: CompanySizeEnum.size_2_10,
        name: '2 - 10',
      },
      {
        id: CompanySizeEnum.size_11_19,
        name: '11 - 19',
      },
      {
        id: CompanySizeEnum.size_20_49,
        name: '20 - 49',
      },
      {
        id: CompanySizeEnum.size_50_99,
        name: '50 - 99',
      },
      {
        id: CompanySizeEnum.size_100_249,
        name: '100 - 249',
      },
      {
        id: CompanySizeEnum.size_250_500,
        name: '250 - 500',
      },
      {
        id: CompanySizeEnum.size_500_more,
        name: '500+',
      },
    ]
  }

  get isNextDisabled() {
    return Boolean(
      !this._companyName.length ||
        this._companyName.length > MAX_COMPANY_NAME_LENGTH ||
        !this._role ||
        !this._companySize
    )
  }

  setCompanyName = (value: string) => {
    if (value.length > MAX_COMPANY_NAME_LENGTH) return

    this._companyName = value.charAt(0).toUpperCase() + value.slice(1)
  }

  setCompanySize = (value: string) => {
    this._companySize = value
  }

  setRole = (role: IRole | null) => {
    if (role && role.name.length > MAX_ROLE_LENGTH) return
    this._role = role
  }

  setActiveField = (value: FieldsEnum | null) => {
    this._activeField = value
  }

  handleBlur = () => {
    this._activeField = null
  }

  handleCompanyNameFieldFocus = () => {
    this.setActiveField(FieldsEnum.companyName)
  }

  handleRoleFieldFocus = () => {
    this.setActiveField(FieldsEnum.role)
  }
}
